import { AppProps } from "next/app";
import Head from "next/head";
import Script from "next/script";
import { MantineProvider } from "@mantine/core";
import { Notifications } from "@mantine/notifications";

import { i18n } from "@lingui/core";
import { I18nProvider } from "@lingui/react";
import { t } from "@lingui/core/macro";

import "@mantine/core/styles.css";
import "@mantine/notifications/styles.css";

// Styles specific to noUI slider
import "nouislider/dist/nouislider.css";
import "../css/slider.css";
// Generated by scripts/fetchFonts.mjs; must precede global.css so the
// @font-face rules are registered before anything references the families.
import "../css/fonts.css";
import "../css/global.css";

import { theme } from "../theme";
import "../utils/i18n";
import PageShell from "../components/PageShell";

export default function App(props: AppProps) {
  const { Component, pageProps } = props;

  return (
    <>
      <Head>
        <title>DeFi Simulator</title>
        <meta
          name="viewport"
          content="minimum-scale=1, initial-scale=1, width=device-width"
        />
        <meta
          name="description"
          content={t`DeFi Simulator is an unofficial, open source, community-built Aave debt simulator and liquidation calculator.`}
        />
        {/* Default share card; share pages override these with snapshot-
            specific tags (later tags win for crawlers within <Head>). */}
        <meta
          key="og:site_name"
          property="og:site_name"
          content="DeFi Simulator"
        />
        <meta key="og:type" property="og:type" content="website" />
        <meta key="og:title" property="og:title" content="DeFi Simulator" />
        <meta
          key="og:description"
          property="og:description"
          content={t`DeFi Simulator is an unofficial, open source, community-built Aave debt simulator and liquidation calculator.`}
        />
        <meta
          key="og:image"
          property="og:image"
          content={`${process.env.NEXT_PUBLIC_SITE_URL || "https://defisim.xyz"}/api/og`}
        />
        <meta key="og:image:width" property="og:image:width" content="1200" />
        <meta key="og:image:height" property="og:image:height" content="630" />
        <meta
          key="twitter:card"
          name="twitter:card"
          content="summary_large_image"
        />
        <meta
          key="twitter:image"
          name="twitter:image"
          content={`${process.env.NEXT_PUBLIC_SITE_URL || "https://defisim.xyz"}/api/og`}
        />
        <link rel="shortcut icon" href="/favicon.ico" />
        {/* Search Console's HTML-tag verification method, for when DNS TXT
            isn't available. Site-wide so verification survives whichever page
            Google fetches. */}
        {process.env.NEXT_PUBLIC_GOOGLE_SITE_VERIFICATION && (
          <meta
            key="google-site-verification"
            name="google-site-verification"
            content={process.env.NEXT_PUBLIC_GOOGLE_SITE_VERIFICATION}
          />
        )}
        {/* No hreflang here: alternates are per-route and must not appear on
            the noindex share pages. components/SeoHead owns them. */}
      </Head>
      {process.env.NODE_ENV === "production" && (
        <Script
          src="https://static.cloudflareinsights.com/beacon.min.js"
          strategy="afterInteractive"
          data-cf-beacon='{"token": "42f927fda7404332a3720866ad63795f"}'
        />
      )}
      <I18nProvider i18n={i18n}>
        <MantineProvider theme={theme} forceColorScheme="dark">
          {/* Pages render *inside* the shell: as siblings, page output landed
              after the footer. The shell passes children straight through on
              the share routes, which supply their own chrome. */}
          <PageShell seo={pageProps.seo}>
            <Component {...pageProps} />
          </PageShell>
          <Notifications />
        </MantineProvider>
      </I18nProvider>
    </>
  );
}
